import { useMutation, useQuery } from '@apollo/client';
import { createContext, useContext, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { GET_CLAIM, UPDATE_CLAIM, GET_RETURN_ORDERS } from './graphql';
import { captureException } from '@sentry/react';
import { useClientUser } from '@/hooks';
import { UserContext } from '@/components/App';

export const Context = createContext();

export const ContextProvider = ({ children }) => {
    const { claim_id } = useParams();
    const [notification, setNotification] = useState({});
    const [editingClaim, editClaim] = useState(null);
    const [modal, setModal] = useState(null);
    const { admin_clients } = useContext(UserContext);
    const { isOnwardAdmin, user_id } = useClientUser();

    const {
        data,
        loading: claimLoading,
        refetch,
    } = useQuery(GET_CLAIM, {
        variables: {
            claim_id,
        },
        onError: (error) => {
            console.error(error);
            captureException(error);
            setNotification({
                severity: 'error',
                message: `Failed to load claim. ${error.message}`,
            });
        },
    });

    const claim = useMemo(() => {
        return data?.claims?.[0] || null;
    }, [data]);

    const { data: returnOrderData, loading: returnOrderLoading } = useQuery(GET_RETURN_ORDERS, {
        variables: {
            original_order_id: claim?.order?.order_id,
        },
    });
    const returnOrders = useMemo(() => {
        return returnOrderData?.orders?.[0] || null;
    }, [returnOrderData]);

    const isEditable = useMemo(() => {
        if (!claim) return false;

        const statusCheck = !['ACCEPTED', 'REJECTED', 'CLOSED'].includes(claim.claim_status);
        if (isOnwardAdmin) return statusCheck;

        const user_ids = !!user_id ? [user_id] : (admin_clients || []).map((client) => client.client_id);
        return user_ids.includes(claim.shipper_id) && statusCheck;
    }, [claim, admin_clients, isOnwardAdmin, user_id]);

    const [updateClaim, { loading: updateClaimLoading }] = useMutation(UPDATE_CLAIM, {
        onError: (error) => {
            console.error(error);
            captureException(error);
            setNotification({
                severity: 'error',
                message: `Failed to update claim. ${error.message}`,
            });
        },
    });

    return (
        <Context.Provider
            value={{
                state: {
                    claim,
                    notification,
                    isEditable,
                    editingClaim,
                    modal,
                    loading: {
                        claim: claimLoading,
                        updateClaim: updateClaimLoading,
                    },
                    returnOrders,
                },
                callbacks: {
                    setNotification,
                    editClaim,
                    setModal,
                    updateClaim: (update) => updateClaim({ variables: { claim_id: claim.claim_id, update } }),
                    refetch,
                },
            }}
        >
            {children}
        </Context.Provider>
    );
};
